/* FT GUYS - variant media: decode before swap, hold until painted, then crossfade

   WHAT HORIZON DOES (assets/media-gallery.js):
     #handleVariantUpdate = (event) => {
       const newMediaGallery = event.detail.data.html.querySelector('media-gallery');
       this.replaceWith(newMediaGallery);
     }
   It destroys the gallery and inserts fresh <img> nodes that have never decoded.

   Sequence on a switch:
     1. Catch variant:update at DOCUMENT CAPTURE - before the gallery's own section
        listener, so nothing has been replaced yet.
     2. Clone the visible image(s): the outgoing frame of the crossfade.
     3. Decode the incoming images (capped, so a slow one cannot freeze the picker).
     4. Replay the event so gallery, price and buttons all update. NEVER dropped -
        an earlier version made it conditional and swallowed intermediate clicks.
     5. Wait for the live image to be paintable, then fade the clones out.

   PRELOAD BUDGET - this is what made the site feel heavy, and it was my doing.

   Warming every colourway meant TWELVE full-size images fetched AND DECODED in the
   background on every product page. Decode is main-thread work: on a phone that is
   enough to make scrolling and tapping sticky, and it buys nothing for the eleven
   colours a visitor never opens. Fetching is cheap and cancellable; decoding is
   neither. That distinction is the whole lesson here.

   The budget is now small and demand-led:
     WARM_LIMIT     how many colours are warmed on idle
     WARM_PARALLEL  one at a time, so it never competes with the page
   Everything else warms on INTENT - hover, touch or focus on a swatch, which lands
   well before the click. A cold colour still swaps correctly; the outgoing frame
   simply holds a little longer while it decodes.

   Raise WARM_LIMIT to restore the old warm-everything behaviour.
*/
(function () {
  'use strict';

  var WARM_LIMIT = 4;
  var WARM_PARALLEL = 1;
  var PRELOAD_WIDTH = 1200;

  var DECODE_VISIBLE_ONLY = true;
  var CANCEL_PREVIOUS = true;

  var DECODE_CAP = 400;
  var FAST_DECODE_CAP = 110;
  var RAPID_WINDOW = 500;
  var PAINT_CAP = 2000;
  var FADE_MS = 500;
  var EASE = 'cubic-bezier(.2,0,0,1)';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var variantImages = new Map();
  var preloaded = new Set();
  var loadedProducts = new Set();
  var mapReady = null;
  var tmplSrcset = null;
  var tmplSizes = null;

  var activeGhost = null;
  var swapToken = 0;
  var lastSwapAt = 0;

  /* 3g added: a mid-range phone on 3g is exactly where warming hurts most */
  function stingy() {
    var c = navigator.connection;
    if (!c) return false;
    if (c.saveData) return true;
    return /(^|-)(2g|slow-2g|3g)$/.test(c.effectiveType || '');
  }

  function sizedUrl(src, width) {
    if (!src) return src;
    try {
      var u = new URL(src, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) { return src; }
  }

  function rebuildSrcset(srcset, newSrc) {
    if (!srcset) return null;
    var out = [];
    srcset.split(',').forEach(function (part) {
      var m = part.trim().match(/^(\S+)\s+(\d+)w$/);
      if (!m) return;
      out.push(sizedUrl(newSrc, parseInt(m[2], 10)) + ' ' + m[2] + 'w');
    });
    return out.length ? out.join(', ') : null;
  }

  function galleryOf(scope) {
    var root = scope || document;
    return root.querySelector ? root.querySelector('media-gallery') : null;
  }

  function onScreen(el) {
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    return r.bottom > 0 && r.top < window.innerHeight;
  }

  function visibleGalleryImages(scope) {
    var gallery = galleryOf(scope);
    if (!gallery) return [];
    var shown = gallery.querySelectorAll('[aria-hidden="false"] .product-media__image');
    var list = shown.length
      ? Array.prototype.slice.call(shown)
      : Array.prototype.slice.call(gallery.querySelectorAll('.product-media__image'));
    return list.filter(function (img) {
      return (img.currentSrc || img.src) && onScreen(img);
    });
  }

  function sampleTemplate() {
    if (tmplSrcset) return;
    var img = visibleGalleryImages(document)[0];
    if (!img) return;
    tmplSrcset = img.getAttribute('srcset') || null;
    tmplSizes = img.getAttribute('sizes') || null;
  }

  function loadProductJson(productUrl) {
    if (!productUrl) return Promise.resolve();
    var clean = productUrl.split('?')[0];
    if (loadedProducts.has(clean)) return mapReady || Promise.resolve();
    loadedProducts.add(clean);
    mapReady = fetch(clean + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.variants) return;
        data.variants.forEach(function (v) {
          var src = v.featured_image && v.featured_image.src;
          if (src) variantImages.set(String(v.id), src);
        });
      })
      .catch(function () {});
    return mapReady;
  }

  function preload(src) {
    if (!src || preloaded.has(src)) return Promise.resolve();
    preloaded.add(src);
    sampleTemplate();
    return new Promise(function (resolve) {
      var img = new Image();
      img.decoding = 'async';
      var set = rebuildSrcset(tmplSrcset, src);
      if (set) {
        if (tmplSizes) img.sizes = tmplSizes;
        img.srcset = set;
      }
      img.onload = function () {
        if (img.decode) img.decode().then(resolve, resolve); else resolve();
      };
      img.onerror = resolve;
      img.src = sizedUrl(src, PRELOAD_WIDTH);
    });
  }

  /* Small, serial and idle-scheduled. */
  function warmSome() {
    if (stingy()) return;
    var urls = [], seen = new Set();
    variantImages.forEach(function (src) {
      if (!seen.has(src)) { seen.add(src); urls.push(src); }
    });
    urls = urls.slice(0, WARM_LIMIT);

    var i = 0;
    function next() {
      if (i >= urls.length) return;
      preload(urls[i++]).then(function () {
        if ('requestIdleCallback' in window) requestIdleCallback(next, { timeout: 1500 });
        else setTimeout(next, 250);
      });
    }
    for (var p = 0; p < WARM_PARALLEL; p++) next();
  }

  function variantIdFrom(el) {
    var node = el.closest('[data-variant-id], label, li');
    if (!node) return null;
    if (node.dataset && node.dataset.variantId) return node.dataset.variantId;
    var input = node.querySelector ? node.querySelector('[data-variant-id]') : null;
    return input ? input.dataset.variantId : null;
  }

  function pickerFrom(el) {
    return el.closest ? el.closest('variant-picker, swatches-variant-picker-component') : null;
  }

  /* Demand-led: hover, touch or focus lands well before the click does. */
  function onIntent(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var picker = pickerFrom(t);
    if (!picker) return;
    var id = variantIdFrom(t);
    if (!id) return;
    var go = function () {
      var src = variantImages.get(String(id));
      if (src) preload(src);
    };
    if (!variantImages.size && picker.dataset && picker.dataset.productUrl) {
      loadProductJson(picker.dataset.productUrl).then(go);
    } else { go(); }
  }

  ['pointerenter', 'pointerdown', 'touchstart', 'focusin'].forEach(function (evt) {
    document.addEventListener(evt, onIntent, { capture: true, passive: true });
  });

  var styleEl = document.createElement('style');
  styleEl.textContent =
    '.ft-media-ghost{pointer-events:none !important;will-change:opacity;' +
    'backface-visibility:hidden;-webkit-backface-visibility:hidden;}';
  document.head.appendChild(styleEl);

  function pin(el, rect) {
    var s = el.style;
    s.setProperty('position', 'fixed', 'important');
    s.setProperty('left', rect.left + 'px', 'important');
    s.setProperty('top', rect.top + 'px', 'important');
    s.setProperty('width', rect.width + 'px', 'important');
    s.setProperty('height', rect.height + 'px', 'important');
    s.setProperty('margin', '0', 'important');
    s.setProperty('padding', '0', 'important');
    s.setProperty('max-width', 'none', 'important');
    s.setProperty('max-height', 'none', 'important');
    s.setProperty('z-index', '4', 'important');
    s.setProperty('opacity', '1', 'important');
    s.setProperty('transform', 'translateZ(0)', 'important');
  }

  function makeGhost(scope) {
    if (reduced) return null;
    var imgs = visibleGalleryImages(scope);
    if (!imgs.length) return null;

    var clones = [];
    imgs.forEach(function (img) {
      var rect = img.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;

      var cs = window.getComputedStyle(img);
      var clone = img.cloneNode(true);
      clone.removeAttribute('ref');
      clone.removeAttribute('id');
      clone.removeAttribute('loading');
      clone.classList.add('ft-media-ghost');
      pin(clone, rect);
      clone.style.setProperty('object-fit', cs.objectFit || 'cover', 'important');
      clone.style.setProperty('object-position', cs.objectPosition || '50% 50%', 'important');
      clone.style.setProperty('border-radius', cs.borderRadius || '0', 'important');
      document.body.appendChild(clone);
      clones.push(clone);
    });

    if (!clones.length) return null;

    var done = false;
    function cleanup() {
      if (done) return;
      done = true;
      clones.forEach(function (c) { c.remove(); });
    }

    return {
      destroy: cleanup,
      release: function (ms) {
        if (done) return;
        var dur = ms || FADE_MS;
        clones.forEach(function (c) {
          c.style.setProperty('transition', 'opacity ' + dur + 'ms ' + EASE, 'important');
        });
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            clones.forEach(function (c) { c.style.setProperty('opacity', '0', 'important'); });
          });
        });
        setTimeout(cleanup, dur + 400);
      }
    };
  }

  function incomingImages(doc) {
    if (!doc || !doc.querySelectorAll) return [];
    if (!DECODE_VISIBLE_ONLY) {
      return Array.prototype.slice.call(doc.querySelectorAll('media-gallery img'));
    }
    var all = Array.prototype.slice.call(doc.querySelectorAll('media-gallery .product-media__image'));
    var wanted = all.filter(function (node) {
      if (node.closest('zoom-dialog, dialog')) return false;
      return !node.closest('[aria-hidden="true"]');
    });
    return wanted.length ? wanted : all;
  }

  function decodeAll(doc, cap) {
    var imgs = incomingImages(doc);
    if (!imgs.length) return Promise.resolve();

    var jobs = imgs.map(function (node) {
      var src = node.getAttribute('src');
      var srcset = node.getAttribute('srcset');
      var sizes = node.getAttribute('sizes');
      if (!src && !srcset) return Promise.resolve();
      return new Promise(function (resolve) {
        var img = new Image();
        img.decoding = 'async';
        if (sizes) img.sizes = sizes;
        if (srcset) img.srcset = srcset;
        if (src) img.src = src;
        if (img.complete && img.naturalWidth) {
          if (img.decode) img.decode().then(resolve, resolve); else resolve();
          return;
        }
        img.onload = function () {
          if (img.decode) img.decode().then(resolve, resolve); else resolve();
        };
        img.onerror = resolve;
      });
    });

    return Promise.race([
      Promise.all(jobs),
      new Promise(function (r) { setTimeout(r, cap); })
    ]);
  }

  function whenPainted(section, previous, token, cb) {
    var start = performance.now();
    (function tick() {
      if (token !== swapToken) return;
      if (performance.now() - start > PAINT_CAP) { cb(); return; }
      var live = visibleGalleryImages(section)[0];
      if (live && live !== previous && live.complete && live.naturalWidth) {
        if (live.decode) live.decode().then(cb, cb);
        else cb();
        return;
      }
      requestAnimationFrame(tick);
    })();
  }

  document.addEventListener('variant:update', function (e) {
    if (e.__ftReady) return;
    if (reduced) return;

    var html = e.detail && e.detail.data && e.detail.data.html;
    var target = e.target;
    if (!html || !target || !target.closest) return;

    var section = target.closest('.shopify-section, dialog') || document;

    e.stopPropagation();

    var now = performance.now();
    var rapid = (now - lastSwapAt) < RAPID_WINDOW;
    lastSwapAt = now;

    var token = ++swapToken;
    var previous = visibleGalleryImages(section)[0] || null;

    if (CANCEL_PREVIOUS && activeGhost) {
      activeGhost.destroy();
      activeGhost = null;
    }

    var ghost = makeGhost(section);
    if (ghost) activeGhost = ghost;

    function fire() {
      var replay = new CustomEvent(e.type, {
        bubbles: true,
        cancelable: e.cancelable,
        composed: true,
        detail: e.detail
      });
      replay.__ftReady = true;
      target.dispatchEvent(replay);

      if (!ghost) return;

      if (rapid) {
        ghost.release(160);
        if (activeGhost === ghost) activeGhost = null;
        return;
      }

      whenPainted(section, previous, token, function () {
        if (token !== swapToken) return;
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            ghost.release();
            if (activeGhost === ghost) activeGhost = null;
          });
        });
      });
    }

    decodeAll(html, rapid ? FAST_DECODE_CAP : DECODE_CAP).then(fire).catch(fire);
  }, true);

  function boot() {
    sampleTemplate();
    var picker = document.querySelector('variant-picker[data-product-url], swatches-variant-picker-component[data-product-url]');
    if (!picker) return;
    loadProductJson(picker.dataset.productUrl).then(warmSome);
  }

  /* later and more patient than before: warming must never be first in the queue */
  function schedule() {
    if ('requestIdleCallback' in window) requestIdleCallback(boot, { timeout: 3000 });
    else setTimeout(boot, 1500);
  }

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });

  document.addEventListener('shopify:section:load', boot);
})();
